import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { UserMinus, UserPlus, UserSearch } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import { MONEY_SCALE, formatMoney, fromString } from '@aeris/money'
import { partyDisplayName } from '@aeris/party'
import { Badge, Button, ConfirmDialog, Section, Spinner } from '@aeris/ui'

import {
  ApiError,
  createPartyForContact,
  fetchContactSuggestions,
  fetchConversationDetail,
  ignoreContact,
  matchContact,
  unlinkContact,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { formatDate } from '../lib/date-format.ts'
import { KIND_LABELS, documentRef } from '../lib/sales-labels.ts'

import { PartyPicker } from './party-picker.tsx'

/**
 * O dinheiro do fio na forma da tela — F248. Era `R$ {valor cru}`: o painel do
 * Atendimento mostrava "R$ 1234.50" para o cliente que estava na conversa.
 */
const money = (wire: string): string =>
  formatMoney(fromString(wire, MONEY_SCALE))

/**
 * Quem é este número (F048 — com tela só agora, na F062).
 *
 * **A camada inteira da F048 existia sem um único consumidor no app web.**
 * Quatro rotas (`suggestions`, `match`, `ignore`, `create-party`), o
 * repositório, o contrato, a permissão `communication.contact.match` e os
 * testes — tudo pronto desde 14/08, e o guia de configuração (F055) mandando
 * a pessoa "confirmar os contatos pendentes" numa tela que não existia. O
 * atendente via um número de telefone no topo da conversa e nada mais: nem o
 * nome do cliente, nem o que ele já comprou, nem como ligar os dois.
 *
 * O painel resolve isso em três estados, e só três:
 *
 *   1. **Vinculado** — nome, documento e o resumo de compras que
 *      `ConversationDetailResponse.party` já trazia (e ninguém lia).
 *   2. **Sem vínculo, com candidatos** — as pessoas cujo telefone bate,
 *      cada uma com "É esta pessoa". Mais "não é nenhuma" e "cadastrar nova".
 *   3. **Sem vínculo, sem candidato** — só "cadastrar nova pessoa", que usa
 *      o nome de perfil do WhatsApp.
 *
 * **Vincular é reversível na prática, mas não é uma ação boba:** ela junta o
 * histórico de compras de uma pessoa a um número de telefone. Por isso
 * "cadastrar nova pessoa" — que cria uma linha em `party` de verdade — passa
 * por `ConfirmDialog`, e confirmar um candidato não passa (é escolher entre
 * opções que o servidor já ofereceu).
 */

const documentLabel = (document: string | null): string =>
  document ?? 'sem documento'

export const CustomerPanel = ({
  conversationId,
  contactId,
}: {
  conversationId: string
  contactId: string
}): ReactNode => {
  const queryClient = useQueryClient()
  const { allows } = useAuth()
  const canMatch = allows('communication.contact.match')

  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [unlinking, setUnlinking] = useState(false)
  /** Procurar o cliente que já existe com outro telefone (F287). */
  const [searching, setSearching] = useState(false)

  const detail = useQuery({
    queryKey: ['communication-conversation-detail', conversationId],
    queryFn: async ({ signal }) =>
      await fetchConversationDetail(conversationId, signal),
  })

  const party = detail.data?.party ?? null

  /**
   * Os candidatos só são buscados quando NÃO há vínculo — perguntar "quem
   * poderia ser?" sobre uma conversa já identificada é uma consulta por
   * telefone que ninguém vai ler.
   */
  const suggestions = useQuery({
    queryKey: ['communication-contact-suggestions', contactId],
    queryFn: async ({ signal }) =>
      await fetchContactSuggestions(contactId, signal),
    enabled: canMatch && detail.isSuccess && party === null,
  })

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ['communication-conversation-detail', conversationId],
    })
    await queryClient.invalidateQueries({
      queryKey: ['communication-contact-suggestions', contactId],
    })
    /*
     * `communication-conversations`, e não `communication-inbox` (F084).
     *
     * A chave escrita aqui na F062 não existe em lugar nenhum: a lista do
     * inbox é `['communication-conversations', { page }]`. Invalidar um nome
     * que ninguém usa não falha nem avisa — só não faz nada, e a lista
     * continuava mostrando o número depois de o atendente vincular o cliente,
     * até a próxima atualização automática.
     */
    await queryClient.invalidateQueries({
      queryKey: ['communication-conversations'],
    })
  }

  const onError = (fallback: string) => (cause: unknown) => {
    setError(cause instanceof ApiError ? cause.message : fallback)
  }

  const link = useMutation({
    mutationFn: async (partyId: string) =>
      await matchContact(contactId, { partyId }),
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: onError('Não foi possível vincular este contato.'),
  })

  const dismiss = useMutation({
    mutationFn: async () => await ignoreContact(contactId),
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: onError('Não foi possível marcar este contato como ignorado.'),
  })

  const create = useMutation({
    mutationFn: async () => await createPartyForContact(contactId),
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: onError('Não foi possível cadastrar esta pessoa.'),
  })

  /**
   * Desfazer o vínculo — a saída que faltava (F084).
   *
   * Vincular era irreversível pela tela. Um número ligado ao cadastro errado
   * — por um automático que casou o telefone de um homônimo, ou por um clique
   * apressado — ficava assim, e o que sobrava ao dono era arquivar a pessoa em
   * Pessoas, o que não desfazia nada.
   */
  const unlink = useMutation({
    mutationFn: async () => await unlinkContact(contactId),
    onSuccess: async () => {
      setError(null)
      setUnlinking(false)
      await invalidate()
    },
    onError: onError('Não foi possível desfazer o vínculo deste contato.'),
  })

  if (detail.isPending) {
    return (
      <Section title="Cliente">
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      </Section>
    )
  }

  if (detail.isError) {
    return (
      <Section title="Cliente">
        <p className="text-(length:--text-sm) text-(--text-muted)">
          Não foi possível carregar os dados do cliente.
        </p>
      </Section>
    )
  }

  if (party !== null) {
    return (
      <Section title="Cliente">
        <div className="flex flex-col gap-1">
          {/*
            `/pessoas` não tem sub-rota por id — a tela de pessoas abre o
            cadastro num diálogo, sem URL própria (o mesmo problema que os
            chamados tinham antes da F061). Link para a lista, e não um
            `to="/pessoas/$id"` que não existe: um link quebrado é pior que
            um link que leva ao lugar certo com um passo a mais.
          */}
          <Link
            to="/pessoas"
            className="text-(length:--text-sm) font-(--weight-medium) text-(--brand-primary) hover:underline"
          >
            {partyDisplayName(party)}
          </Link>
          <span className="text-(length:--text-xs) text-(--text-muted)">
            {documentLabel(party.document)}
          </span>
          {/*
            A carteira (F210): quando o cliente é de outra pessoa, quem atende
            sabe. Só aviso — quem decide passar a conversa é o gerente.
          */}
          {party.salespersonName === null ? null : (
            <Badge
              tone={
                party.salespersonUserId === detail.data.assigneeEmployeeId
                  ? 'neutral'
                  : 'warning'
              }
            >
              Cliente de {party.salespersonName}
            </Badge>
          )}
        </div>

        <dl className="grid grid-cols-2 gap-2">
          <div>
            <dt className="text-(length:--text-xs) text-(--text-muted)">
              Documentos
            </dt>
            <dd className="text-(length:--text-sm)">
              {party.purchases.totalDocuments}
            </dd>
          </div>
          <div>
            <dt className="text-(length:--text-xs) text-(--text-muted)">
              Total comprado
            </dt>
            <dd className="text-(length:--text-sm)">
              {money(party.purchases.totalAmount)}
            </dd>
          </div>
        </dl>

        {/*
          O crédito (F085) — o mesmo cálculo que o PDV usa para decidir uma
          venda fiado, não uma soma escrita aqui.

          Quem responde no WhatsApp costuma ser quem vai fechar a venda, e a
          pergunta "posso mandar sem pedir o dinheiro antes?" é decidida
          exatamente por estes três números. Ir ao financeiro para descobrir
          isso no meio de uma conversa é o tipo de ida e volta que faz a pessoa
          desistir de perguntar.

          Bloqueio e atraso aparecem primeiro, e destacados: são fatos que
          MUDAM a conversa, não números de referência.
        */}
        {party.credit === null ? null : (
          <div className="flex flex-col gap-1.5 rounded-(--radius-md) bg-(--surface-sunken) p-2">
            {party.credit.blocked ? (
              <p className="text-(length:--text-xs) font-(--weight-medium) text-(--danger)">
                Crédito bloqueado
              </p>
            ) : null}

            {party.credit.overdueCount > 0 ? (
              <p className="text-(length:--text-xs) font-(--weight-medium) text-(--warning)">
                {party.credit.overdueCount} título
                {party.credit.overdueCount === 1 ? '' : 's'} em atraso
                {party.credit.oldestOverdueDays > 0
                  ? ` — o mais antigo há ${String(party.credit.oldestOverdueDays)} dia${party.credit.oldestOverdueDays === 1 ? '' : 's'}`
                  : ''}
              </p>
            ) : null}

            <dl className="grid grid-cols-2 gap-2">
              <div>
                <dt className="text-(length:--text-xs) text-(--text-muted)">
                  Em aberto
                </dt>
                <dd className="text-(length:--text-sm)">
                  {money(party.credit.outstanding)}
                </dd>
              </div>
              <div>
                <dt className="text-(length:--text-xs) text-(--text-muted)">
                  {party.credit.limit === null ? 'Crédito' : 'Disponível'}
                </dt>
                <dd className="text-(length:--text-sm)">
                  {/*
                    Limite nulo é "sem crédito concedido", NUNCA "ilimitado"
                    (ADR-0022) — e as duas frases mandam a pessoa para
                    conversas diferentes.
                  */}
                  {party.credit.limit === null
                    ? 'não concedido'
                    : money(party.credit.available ?? '0')}
                </dd>
              </div>
            </dl>
          </div>
        )}

        {party.purchases.recent.length === 0 ? (
          <p className="text-(length:--text-xs) text-(--text-muted)">
            Nenhuma compra registrada.
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {party.purchases.recent.map((document) => (
              <li
                key={document.id}
                className="flex items-center justify-between gap-2 text-(length:--text-xs)"
              >
                <span className="text-(--text-secondary)">
                  {/* "Pedido nº 000016", e não "#000016" nem "order" (F248). */}
                  {document.number === null
                    ? `${KIND_LABELS[document.kind]} (rascunho)`
                    : documentRef(document.kind, document.number)}{' '}
                  · {formatDate(document.issuedAt)}
                </span>
                <span className="text-(--text-primary)">
                  {money(document.totalAmount)}
                </span>
              </li>
            ))}
          </ul>
        )}

        {/*
          Desfazer o vínculo passa por confirmação, pelo mesmo raciocínio que
          "cadastrar nova pessoa" passa: a ação separa um histórico de compras
          de um número de telefone, e quem clicar sem querer perde a referência
          de quem é o cliente no meio de um atendimento.
        */}
        {canMatch ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="self-start"
              disabled={unlink.isPending}
              leadingIcon={<UserMinus aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                setUnlinking(true)
              }}
            >
              Desvincular
            </Button>

            <ConfirmDialog
              open={unlinking}
              onOpenChange={setUnlinking}
              title="Desvincular este cliente?"
              description="O número volta a aparecer como não identificado, e a tela oferece de novo os clientes possíveis. Nenhum cadastro é apagado e nenhuma compra é perdida."
              confirmLabel="Desvincular"
              loading={unlink.isPending}
              onConfirm={() => {
                unlink.mutate()
              }}
            />
          </>
        ) : null}

        {error === null ? null : (
          <p role="alert" className="text-(length:--text-xs) text-(--danger)">
            {error}
          </p>
        )}
      </Section>
    )
  }

  if (!canMatch) {
    return (
      <Section title="Cliente">
        <p className="text-(length:--text-sm) text-(--text-muted)">
          Este número ainda não está ligado a nenhum cliente. Peça a um
          administrador para identificá-lo.
        </p>
      </Section>
    )
  }

  const candidates = suggestions.data?.candidates ?? []

  return (
    <Section
      title="Quem é este número?"
      description="Ligar o número a um cliente traz o histórico de compras para dentro da conversa."
    >
      {suggestions.isPending ? (
        <div className="flex justify-center py-2">
          <Spinner />
        </div>
      ) : candidates.length === 0 ? (
        <p className="flex items-start gap-2 text-(length:--text-sm) text-(--text-muted)">
          <UserSearch aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
          Nenhum cliente cadastrado tem este telefone.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {candidates.map((candidate) => (
            <li
              key={candidate.id}
              className="flex items-center justify-between gap-2 rounded-(--radius-md) border border-(--border-default) px-3 py-2"
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-(length:--text-sm) text-(--text-primary)">
                  {partyDisplayName(candidate)}
                </span>
                <span className="text-(length:--text-xs) text-(--text-muted)">
                  {documentLabel(candidate.document)}
                </span>
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={link.isPending}
                onClick={() => {
                  link.mutate(candidate.id)
                }}
              >
                É esta pessoa
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/*
        O cliente que já tem cadastro com OUTRO telefone (F287): a sugestão
        acima só acha pelo número, e "Cadastrar pessoa nova" duplicava quem já
        estava em Pessoas. Procurar pelo nome ou documento e ligar.
      */}
      {searching ? (
        <PartyPicker
          value=""
          role="customer"
          placeholder="Nome, apelido ou CNPJ do cliente"
          disabled={link.isPending}
          onChange={(partyId) => {
            if (partyId === '') return
            setSearching(false)
            link.mutate(partyId)
          }}
        />
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leadingIcon={<UserSearch aria-hidden="true" className="h-4 w-4" />}
          onClick={() => {
            setSearching((open) => !open)
          }}
        >
          {searching ? 'Fechar a busca' : 'Procurar cliente já cadastrado'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leadingIcon={<UserPlus aria-hidden="true" className="h-4 w-4" />}
          loading={create.isPending}
          onClick={() => {
            setCreating(true)
          }}
        >
          Cadastrar pessoa nova
        </Button>
        {candidates.length === 0 ? null : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            loading={dismiss.isPending}
            onClick={() => {
              dismiss.mutate()
            }}
          >
            Não é nenhuma delas
          </Button>
        )}
      </div>

      {error === null ? null : (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {error}
        </p>
      )}

      <ConfirmDialog
        open={creating}
        onOpenChange={setCreating}
        tone="neutral"
        title="Cadastrar uma pessoa nova para este número?"
        description="Ela nasce com o nome do perfil do WhatsApp e sem documento — o cadastro completo fica para depois, em Pessoas. O número já fica ligado a ela."
        confirmLabel="Cadastrar"
        loading={create.isPending}
        onConfirm={() => {
          setCreating(false)
          create.mutate()
        }}
      />
    </Section>
  )
}
